import { Quote } from "@/models/Quote";
import { BillingAddressType } from "@/models/PaymentMethod";
import {
  formatAmount,
  formatDate,
  formatPhoneNumber,
  formatAmountWithoutCents,
  formatPaymentOption,
  calculateQuoteTotal,
} from "@/utils/Utils";
import { DateTimeFormatter, LocalDate } from "@js-joda/core";
import instanceAxios from "./axiosInst";

export class ReceiptService {
  static getPropertyAddressString(quote: Quote): string {
    return (
      quote.address.address +
      "<br>" +
      quote.address.city +
      " " +
      quote.address.state.title +
      " " +
      quote.address.zip
    );
  }
  static getMailingAddressString(quote: Quote): string {
    if (quote.checkout.useSameAddressForMailing === true) {
      return this.getPropertyAddressString(quote);
    }
    return (
      quote.checkout.mailingAddress.streetNumber +
      " " +
      quote.checkout.mailingAddress.address +
      "<br>" +
      quote.checkout.mailingAddress.city +
      " " +
      quote.checkout.mailingAddress.state.title +
      " " +
      quote.checkout.mailingAddress.zip
    );
  }
  static getInterestedPartyString(quote: Quote): string {
    if (quote.checkout.interestedParty.firstName !== "") {
      quote.checkout.interestedParty.firstName +
      " " +
      quote.checkout.interestedParty.lastName +
      "<br>" +
      quote.checkout.interestedParty.address.address +
      " " +
      quote.checkout.interestedParty.address.apt
        ? ", Apt " + quote.checkout.interestedParty.address.apt
        : "" +
          "<br>" +
          quote.checkout.interestedParty.address.city +
          ", " +
          quote.checkout.interestedParty.address.state.title +
          " " +
          quote.checkout.interestedParty.address.zip;
    }
    return "";
  }

  static getRoomateString(quote: Quote): string {
    if (quote.checkout.roommates.length > 0) {
      return (
        quote.checkout.roommates[0].firstName +
        " " +
        quote.checkout.roommates[0].lastName
      );
    }
    return "";
  }

  static getBillingAddress(quote: Quote) {
    if (
      quote.checkout.paymentMethod.billingAddressType ==
      BillingAddressType.Property
    ) {
      return quote.address;
    } else if (
      quote.checkout.paymentMethod.billingAddressType ==
      BillingAddressType.Mailing
    ) {
      if (quote.checkout.useSameAddressForMailing) {
        return quote.address;
      } else {
        return quote.checkout.mailingAddress;
      }
    } else if (
      quote.checkout.paymentMethod.billingAddressType ==
      BillingAddressType.Different
    ) {
      return quote.checkout.paymentMethod.billingAddress;
    } else {
      return quote.address;
    }
  }

  static async getReceiptLink(
    quote: Quote
  ): Promise<{ error: boolean; message: string; result: any }> {
    const result = {
      error: false,
      message: "",
      result: {},
    };

    const biladdress = this.getBillingAddress(quote);

    const data: any = {
      application: {
        coverageDetails: {
          rentersInsurancePolicy: quote.rentersInsurance.policyNumber,
          policyTerm: quote.info.policyLength + " Year",
          coverageStartDate: formatDate(quote.info.coverageStartDate),
          yourLiability: formatAmountWithoutCents(
            quote.rentersInsurance.quoteCoverageDetails.liabilityAmount
          ),
          personalPropertyCoverage: formatAmountWithoutCents(
            quote.rentersInsurance.quoteCoverageDetails.personalPropertyAmount
          ),
          paymentPlan: formatPaymentOption(
            quote.rentersInsurance.paymentOption
          ),
          replacementCost: "Included", // check this
          accidentalDamagePersonalPropertyAddOn: "Added", //..............
          accidentalDamagePersonalPropertyAddOnPolicy:
            quote.personalInsurance.coverageAmount,
          accidentalDamagePersonalPropertyCoverage:
            quote.personalInsurance.coverageAmount,
          accidentalDamagePersonalPropertyDeductible:
            quote.personalInsurance.deductibleAmount,
          accidentalDamagePersonalPropertyPaymentPlan:
            quote.personalInsurance.paymentOption, // check this and below also
          schoolInformation:
            quote.checkout.schoolName + "<br>" + quote.checkout.schoolCity, //.................
        },
        yourInformation: {
          policyHolder:
            quote.checkout.info.firstName + " " + quote.checkout.info.lastName,
          dateOfBirth: formatDate(quote.checkout.info.dob),
          phoneNumber: formatPhoneNumber(quote.checkout.info.phone),
          email: quote.checkout.info.email,
          propertyAddress: this.getPropertyAddressString(quote),
          mailingAddress: this.getMailingAddressString(quote),
        },
        hasPersonalInsurance: quote.hasPersonalInsurance,
      },
      order: {
        customerDetailsStatic: {
          orderPlaced: new Date().toLocaleDateString(),
          insured:
            quote.checkout.info.firstName + " " + quote.checkout.info.lastName,
          policyTerm: quote.info.policyLength + " Year",
          billedTo: biladdress.firstName + " " + biladdress.lastName,
          billedStreet:
            biladdress.address +
            ", " +
            biladdress.city +
            ", " +
            biladdress.state.title +
            ", " +
            biladdress.zip,
          creditCard:
            "**** " +
            quote.checkout.paymentMethod.cardNumber.substr(
              quote.checkout.paymentMethod.cardNumber.length - 4
            ),
          paidToday: formatAmount(calculateQuoteTotal(quote)),
        },
        assurantRentersInsuranceDetails: {
          //  "successful": quote.assurantPassed,
          // "data": {
          rentersInsurance:
            formatAmount(
              quote.rentersInsurance.quoteCoverageDetails.liabilityAmount
            ) +
            "/" +
            formatAmount(
              quote.rentersInsurance.quoteCoverageDetails.personalPropertyAmount
            ) +
            "/" +
            formatAmount(quote.rentersInsurance.dueToday) +
            " " +
            formatPaymentOption(quote.rentersInsurance.paymentOption),
          coverageStartDate: LocalDate.parse(
            quote.info.coverageStartDate
          ).format(DateTimeFormatter.ofPattern("M/d/yyyy")),
          policyNumber: quote.rentersInsurance.policyNumber,
          //}
        },
        nssiInsuranceDetails: {
          transId: quote.personalInsurance.transactionId,
          accidentalDamagePersonalPropertyCoverageAddOn:
            "$" +
            quote.personalInsurance.coverageAmount +
            "/$" +
            quote.personalInsurance.annualAmount +
            " " +
            quote.personalInsurance.paymentOption,
          coverageStartDate: quote.info.coverageStartDate,
        },
      },
    };

    if (
      quote.rentersInsurance.quoteCoverageDetails.hurricaneDeductible != null
    ) {
      data["application"]["coverageDetails"]["Hurricane Deductible"] =
        formatAmountWithoutCents(
          quote.rentersInsurance.quoteCoverageDetails.hurricaneDeductible
        );
    }

    if (quote.rentersInsurance.quoteCoverageDetails.replacementCost) {
      data["application"]["coverageDetails"]["replacementCost"] = "Included";
    }

    if (quote.rentersInsurance.quoteCoverageDetails.waterBackup) {
      data["application"]["coverageDetails"]["waterSewerBackup"] = "Added";
    }

    if (quote.rentersInsurance.quoteCoverageDetails.identityFraud) {
      data["application"]["coverageDetails"]["Fraud Expense Protection"] =
        "Added";
    }

    if (quote.rentersInsurance.quoteCoverageDetails.earthquake) {
      data["application"]["coverageDetails"]["Earthquake"] = "Added";
    }

    if (quote.hasPersonalInsurance === true) {
      data["application"]["coverageDetails"][
        "accidentalDamagePersonalPropertyAddOn"
      ] = "Added";
      //data["application"]["coverageDetails"]["accidentalDamagePersonalPropertyAddOnPolicy"] = quote.personalInsurance.policyNumber ? quote.personalInsurance.policyNumber : "N/A";
      data["application"]["coverageDetails"][
        "accidentalDamagePersonalPropertyCoverage"
      ] = formatAmountWithoutCents(quote.personalInsurance.coverageAmount);
      data["application"]["coverageDetails"][
        "accidentalDamagePersonalPropertyDeductible"
      ] = formatAmountWithoutCents(quote.personalInsurance.deductibleAmount);
      data["application"]["coverageDetails"][
        "accidentalDamagePersonalPropertyPaymentPlan"
      ] = formatPaymentOption(quote.personalInsurance.paymentOption);
      data["application"]["coverageDetails"]["schoolInformation"] =
        quote.checkout.schoolName + "<br>" + quote.checkout.schoolCity;
    }

    if (quote.checkout.roommates.length > 0) {
      data["application"]["yourInformation"]["Roommate"] =
        this.getRoomateString(quote);
    }

    if (quote.checkout.interestedParty.firstName !== "") {
      data["application"]["yourInformation"]["Interested Party"] =
        this.getInterestedPartyString(quote);
    }

    // if (quote.hasPersonalInsurance == true) {
    //   data["order"]["nssiInsuranceDetails"] = {};
    //   data["order"]["nssiInsuranceDetails"]["orderNumber"] = quote.hasPersonalInsurance ? (quote.personalInsurance.transactionId ? quote.personalInsurance.transactionId : "N/A") : "N/A";
    //   data["order"]["nssiInsuranceDetails"]["successful"] = quote.nssiPassed;
    //   data["order"]["nssiInsuranceDetails"]["data"] = {};
    //   data["order"]["nssiInsuranceDetails"]["data"]["accidentalDamagePersonalPropertyAddOn"] = quote.hasPersonalInsurance ? (formatAmount(quote.personalInsurance.coverageAmount)
    //     + "/" + formatAmount(quote.personalInsurance.dueToday) + " " + formatPaymentOption(quote.personalInsurance.paymentOption)) : "N/A";
    //   data["order"]["nssiInsuranceDetails"]["data"]["coverageStartDate"] =quote.hasPersonalInsurance ? (LocalDate.now().format(DateTimeFormatter.ofPattern("M/d/yyyy"))) : "N/A";
    //   data["order"]["nssiInsuranceDetails"]["data"]["policyNumber"] = quote.hasPersonalInsurance ? (quote.personalInsurance.policyNumber ? quote.personalInsurance.policyNumber : "N/A") : "N/A";
    // }

    let response;
    try {
      response = await instanceAxios.post(
        process.env.VUE_APP_MAGENTO_URL + "/rest/V1/NSSI/create/pdfInvoice",
        data
      );
    } catch (error) {
      result.error = true;
      result.message = "Unknown error";
    }
    result.result = response?.data;
    return new Promise<{ error: boolean; message: string; result: any }>(
      (resolve) => {
        resolve(result);
      }
    );
  }
}
